import { print } from 'graphql';
import { useMutation, useQueryClient } from 'react-query';
import {
  createCustomField as createCustomFieldQuery,
  updateCustomField as updateCustomFieldQuery,
  deactivateCustomField as deactivateCustomFieldQuery,
  reactivateCustomField as reactivateCustomFieldQuery,
  reorderCustomFields as reorderCustomFieldsQuery,
} from 'src/graphql/customFields/customFields';
import { fetcher } from 'src/utils/fetch';

export interface CreateCustomFieldInput {
  label: string;
  identifier?: string;
  description?: string | null;
  fieldType: 'TEXT' | 'SINGLE_SELECT';
  options?: string[];
  sectionId?: string | null;
}

export interface CreateCustomFieldResponse {
  createCustomField: {
    id: string;
    label: string;
    identifier: string;
    description: string | null;
    fieldType: string;
    status: string;
    displayOrder: number;
  };
}

export const useCustomFieldMutations = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: createCustomField, isLoading: isCreating } = useMutation<
    CreateCustomFieldResponse,
    Error,
    CreateCustomFieldInput
  >(
    async (input) => {
      const fetcherFn = fetcher<
        CreateCustomFieldResponse,
        { input: CreateCustomFieldInput }
      >(print(createCustomFieldQuery), { input });
      return fetcherFn();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['GetOrganisationCustomFields']);
        queryClient.invalidateQueries(['GetOrganisationCustomFieldSections']);
      },
    },
  );

  const { mutateAsync: updateCustomField, isLoading: isUpdating } = useMutation<
    CreateCustomFieldResponse,
    Error,
    {
      id: string;
      label?: string;
      description?: string | null;
      sectionId?: string | null;
    }
  >(
    async (input) => {
      const fetcherFn = fetcher<
        CreateCustomFieldResponse,
        {
          input: {
            id: string;
            label?: string;
            description?: string | null;
            sectionId?: string | null;
          };
        }
      >(print(updateCustomFieldQuery), { input });
      return fetcherFn();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['GetOrganisationCustomFields']);
        queryClient.invalidateQueries(['GetOrganisationCustomFieldSections']);
      },
    },
  );

  const { mutateAsync: deactivateCustomField, isLoading: isDeactivating } =
    useMutation<{ deactivateCustomField: boolean }, Error, { id: string }>(
      async (input) => {
        const fetcherFn = fetcher<
          { deactivateCustomField: boolean },
          { input: { id: string } }
        >(print(deactivateCustomFieldQuery), { input });
        return fetcherFn();
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['GetOrganisationCustomFields']);
          queryClient.invalidateQueries(['GetOrganisationCustomFieldSections']);
        },
      },
    );

  const { mutateAsync: reactivateCustomField, isLoading: isReactivating } =
    useMutation<{ reactivateCustomField: boolean }, Error, { id: string }>(
      async (input) => {
        const fetcherFn = fetcher<
          { reactivateCustomField: boolean },
          { input: { id: string } }
        >(print(reactivateCustomFieldQuery), { input });
        return fetcherFn();
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['GetOrganisationCustomFields']);
          queryClient.invalidateQueries(['GetOrganisationCustomFieldSections']);
        },
      },
    );

  const { mutateAsync: reorderCustomFields, isLoading: isReordering } =
    useMutation<
      { reorderCustomFields: Array<{ id: string; displayOrder: number }> },
      Error,
      {
        draggedFieldId: string;
        values: Array<{ id: string; displayOrder: number }>;
      }
    >(
      async (input) => {
        const fetcherFn = fetcher<
          { reorderCustomFields: Array<{ id: string; displayOrder: number }> },
          {
            input: {
              draggedFieldId: string;
              values: Array<{ id: string; displayOrder: number }>;
            };
          }
        >(print(reorderCustomFieldsQuery), { input });
        return fetcherFn();
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['GetOrganisationCustomFields']);
          queryClient.invalidateQueries(['GetOrganisationCustomFieldSections']);
        },
      },
    );

  return {
    createCustomField,
    updateCustomField,
    deactivateCustomField,
    reactivateCustomField,
    reorderCustomFields,
    isCreating,
    isUpdating,
    isDeactivating,
    isReactivating,
    isReordering,
    isLoading:
      isCreating ||
      isUpdating ||
      isDeactivating ||
      isReactivating ||
      isReordering,
  };
};
